import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  CalendarPlus,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AppointmentPaymentTabs } from "@/components/appointments/AppointmentPaymentTabs";
import { ReceptionPaymentLinkPanel } from "@/components/settings/ReceptionPaymentLinkPanel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { appointmentsAPI } from "@/lib/appointments-api";
import { queryKeys } from "@/lib/query-keys";

const statePresentation = {
  creating: { label: "Preparing link", variant: "neutral" },
  awaiting_payment: { label: "Awaiting payment", variant: "warning" },
  payment_in_progress: { label: "Payment in progress", variant: "warning" },
  cancellation_pending: {
    label: "Cancellation pending",
    variant: "warning",
  },
  resolution_pending: { label: "Needs provider check", variant: "critical" },
} as const;

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

const formatReason = (value: string) =>
  value.replaceAll("_", " ").replace(/^./, (letter) => letter.toUpperCase());

export const AwaitingPaymentsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<
    string | null
  >(null);
  const page = 1;
  const pageSize = 50;
  const awaiting = useQuery({
    queryKey: queryKeys.appointments.awaitingPayments(page, pageSize),
    queryFn: () => appointmentsAPI.getAwaitingPayments(page, pageSize),
    refetchInterval: 10000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  if (selectedAppointmentId) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        <AppointmentPaymentTabs activeTab="awaiting-payment" />
        <div className="mx-auto w-full max-w-4xl space-y-4">
          <Button
            variant="ghost"
            className="z-button text-muted-foreground hover:text-foreground"
            onClick={() => setSelectedAppointmentId(null)}
          >
            <ChevronLeft className="mr-1 h-4 w-4" /> Back to awaiting payments
          </Button>
          <ReceptionPaymentLinkPanel
            appointmentId={selectedAppointmentId}
            onFinished={() => {
              setSelectedAppointmentId(null);
              void queryClient.invalidateQueries({
                queryKey: queryKeys.appointments.all,
              });
            }}
            onRestart={() => setSelectedAppointmentId(null)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <AppointmentPaymentTabs activeTab="awaiting-payment" />
      <Card className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-3xl border-border/60 shadow-sm">
        <CardHeader className="gap-4 border-b border-border/60 bg-card p-5 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Clock3 className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="z-h1 flex items-center gap-2 text-foreground">
                Awaiting payment
                <Badge variant="neutral" className="z-caption z-numeric">
                  {awaiting.data?.count ?? 0}
                </Badge>
              </CardTitle>
              <p className="z-secondary mt-1 text-muted-foreground">
                Attention states appear first, followed by the nearest payment
                deadline.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 sm:justify-end">
            <Button
              size="sm"
              className="z-button"
              onClick={() => navigate("/settings/appointments/add-appointment")}
            >
              <CalendarPlus className="mr-2 h-4 w-4" /> Book appointment
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="z-button border-border/70 bg-background/80 shadow-none"
              disabled={awaiting.isFetching}
              onClick={() => void awaiting.refetch()}
            >
              <RefreshCw
                className={`mr-2 h-4 w-4 ${awaiting.isFetching ? "animate-spin" : ""}`}
              />
              Refresh list
            </Button>
          </div>
        </CardHeader>
        <CardContent className="min-h-0 flex-1 overflow-y-auto p-4">
          {awaiting.isLoading ? (
            <div className="flex min-h-64 flex-col items-center justify-center gap-3 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
              <p className="z-secondary">Loading payment queue…</p>
            </div>
          ) : awaiting.error ? (
            <div className="flex min-h-64 flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-destructive/30 bg-destructive/5 p-6 text-center">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                <AlertCircle className="h-5 w-5" />
              </div>
              <div>
                <p className="z-body-md text-foreground">
                  Unable to load awaiting payments
                </p>
                <p className="z-secondary mt-1 text-muted-foreground">
                  Check the connection and try the list again.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => awaiting.refetch()}
              >
                Try again
              </Button>
            </div>
          ) : awaiting.data?.results.length ? (
            <div className="space-y-3">
              {awaiting.data.results.map((item) => {
                const presentation =
                  statePresentation[
                    item.state as keyof typeof statePresentation
                  ];
                return (
                  <button
                    key={item.session_id}
                    type="button"
                    onClick={() =>
                      setSelectedAppointmentId(item.appointment_id)
                    }
                    className="group grid w-full gap-4 rounded-2xl border border-border/60 bg-background/55 p-4 text-left shadow-sm transition-colors hover:border-border hover:bg-accent/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] lg:grid-cols-[minmax(0,1.3fr)_minmax(0,0.9fr)_minmax(0,1fr)_auto] lg:items-center"
                  >
                    <div className="min-w-0">
                      <p className="z-body-md truncate text-foreground">
                        {item.patient_name ?? "Unknown patient"}
                      </p>
                      <p className="z-secondary mt-1 truncate text-muted-foreground">
                        {item.service_name}
                      </p>
                      <p className="z-caption z-numeric mt-1 text-muted-foreground">
                        Appointment{" "}
                        {formatDateTime(item.appointment_start_time)}
                      </p>
                    </div>
                    <div className="min-w-0">
                      <Badge
                        variant={presentation?.variant ?? "neutral"}
                        className="z-caption"
                      >
                        {presentation?.label ?? formatReason(item.state)}
                      </Badge>
                      {item.safe_reason ? (
                        <p className="z-caption mt-1.5 truncate text-muted-foreground">
                          {formatReason(item.safe_reason)}
                        </p>
                      ) : null}
                    </div>
                    <div className="z-caption z-numeric space-y-1 text-muted-foreground">
                      <p>Link ends {formatDateTime(item.link_expires_at)}</p>
                      <p>Final check {formatDateTime(item.slot_release_at)}</p>
                      <p>
                        Last checked{" "}
                        {item.last_checked_at
                          ? formatDateTime(item.last_checked_at)
                          : "not yet"}
                      </p>
                    </div>
                    <div className="flex items-center justify-between gap-3 lg:justify-end">
                      <span className="z-numeric font-semibold text-foreground">
                        {item.currency} {(item.amount_paise / 100).toFixed(2)}
                      </span>
                      <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-foreground" />
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="flex min-h-64 flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border/70 bg-muted/15 p-6 text-center">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-muted text-muted-foreground">
                <Clock3 className="h-5 w-5" />
              </div>
              <div>
                <p className="z-body-md text-foreground">
                  No payments need attention
                </p>
                <p className="z-secondary mt-1 text-muted-foreground">
                  New unpaid Reception bookings will appear here automatically.
                </p>
              </div>
              <Button
                size="sm"
                onClick={() =>
                  navigate("/settings/appointments/add-appointment")
                }
              >
                <CalendarPlus className="mr-2 h-4 w-4" /> Book appointment
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
